import { useEffect, useRef, type FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'

import type { DashboardContextValue } from '@pages/IntroducingBroker/DashboardPoC/DashboardWrapper'
import { DashboardGrid } from '@pages/IntroducingBroker/DashboardPoC/components/DashboardGrid'
import { EditMode } from '@pages/IntroducingBroker/DashboardPoC/components/EditMode'

import { useModal } from '@context/ModalsContext'

import { Title1 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { Divider } from '@ui/components/Divider'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { Scrollbar } from '@ui/v2/Scrollbar/Scrollbar'

import { Box } from '@shared/components/Box'
import { ActionModal } from '@shared/modals/Action.modal'
import { FullScreenLayout } from '@shared/templates/FullScreenLayout'

import styles from './IBDashboardEditPage.module.scss'

export const IBDashboardEditPage: FC = () => {
  const { t } = useTranslation()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const {
    isLoading,
    isMobile,
    currentLayout,
    visibleWidgets,
    handleLayoutChange,
    handleRemoveWidgetByType,
    handleOpenWidgetPalette,
    handleSave,
    handleEditLayoutMetadata,
    layoutCRUDActionModal,
    editLayoutModal,
    handleEditsCancel,
  } = useOutletContext<DashboardContextValue>()

  const exitModal = useModal(ActionModal, {
    onConfirm: handleEditsCancel,
  })

  useEffect(() => {
    if (isMobile) {
      handleEditsCancel()
    }
  }, [handleEditsCancel, isMobile])

  return (
    <FullScreenLayout
      className={styles.dashboardContent}
      isContentScrollable={false}
      isLoading={isLoading}
      maxContentWidth={1232}
      headerLeftSlot={
        !isMobile ? (
          <Box flex align='center' gap='x16' fullHeight>
            <IconButton.Secondary
              aria-label={t('Back')}
              size='sm'
              icon={ArrowIcon.Left}
              onClick={() => exitModal.open()}
            />
            <Divider.Vertical />
            <Title1 medium color='text-high-em'>
              {t('Dashboard.Edit Mode')}
            </Title1>
          </Box>
        ) : undefined
      }
      headerRightSlot={
        !isMobile ? (
          <div className={styles.headerActions}>
            <Button.Danger fullWidth size='sm' onClick={() => exitModal.open()}>
              {t('Cancel')}
            </Button.Danger>
            {!currentLayout?.isPreset && (
              <Button.Primary
                fullWidth
                size='sm'
                onClick={() =>
                  layoutCRUDActionModal.open({
                    actionEvent: 'save',
                    onAccept: handleSave,
                  })
                }
              >
                {t('Dashboard.Done')}
              </Button.Primary>
            )}
          </div>
        ) : undefined
      }
    >
      <Box flex fullWidth gap='x24' direction='column' fullHeight minHeight='0'>
        <div className={styles.dashboardGridHost}>
          <div ref={scrollContainerRef} className={styles.dashboardGridScroller}>
            <DashboardGrid
              widgets={visibleWidgets}
              isEditMode
              onLayoutChange={handleLayoutChange}
              onRemoveWidget={handleRemoveWidgetByType}
            />
          </div>
          <Scrollbar
            position='outside'
            parentRef={scrollContainerRef}
            size='sm'
            orientation='vertical'
          />
        </div>

        <EditMode
          showEditModal={() =>
            editLayoutModal.open({
              onSave: handleEditLayoutMetadata,
              currentName: currentLayout?.name ?? '',
              currentDescription: currentLayout?.description ?? '',
            })
          }
          showWidgetPalette={handleOpenWidgetPalette}
        />
      </Box>
    </FullScreenLayout>
  )
}
